import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { MenuItem, Text } from '@actualtysoft/ui-kit';
import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  useTheme,
} from '@mui/material';
import { NavRoute } from 'data/models/navigation.interface';
import isEmpty from 'lodash/isEmpty';
import { flatRoutes } from 'plugins/router';
import { useUserStore } from 'stores/user';

interface Props {
  navRoute: NavRoute;
  level?: number;
  path?: string;
  closeParentMenu?: () => void;
}

const Navigation = ({
  navRoute,
  level = 1,
  path = navRoute.path,
  closeParentMenu = () => null,
}: Props): JSX.Element => {
  const { palette } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const { user } = useUserStore();
  const activeRoute = useMemo(
    () => flatRoutes.find(({ path }) => pathname === path),
    [pathname],
  );
  const isActive =
    activeRoute?.ancestors?.find(({ name }) => navRoute.name === name) && true;

  if (
    navRoute.hidden ||
    (navRoute.permissions &&
      !navRoute.permissions.some((permission) =>
        user.permissions.includes(permission),
      ))
  ) {
    return <></>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openMenu = (e: any) => {
    setAnchor(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchor(null);
    closeParentMenu();
  };

  if (isEmpty(navRoute.children)) {
    return (
      <Box>
        <MenuItem
          selected={isActive}
          onClick={() => {
            closeMenu();
            navigate(path);
          }}
          sx={{
            '&:hover': {
              svg: {
                color: palette.primary.main,
              },
            },
            '&.Mui-selected': {
              svg: {
                color: palette.primary.dark,
              },
            },
            svg: {
              color: palette.redActual,
            },
          }}
        >
          <Box display="inline-flex" alignItems="center">
            {navRoute.icon}
            <Text variant="body2">{navRoute.name}</Text>
          </Box>
        </MenuItem>
      </Box>
    );
  }

  return (
    <div>
      <ListItem
        component="button"
        onClick={openMenu}
        alignItems="center"
        style={{ cursor: 'pointer' }}
      >
        <ListItemIcon
          sx={{
            minWidth: '25px',
            svg: {
              color: '#e20e28 !important',
            },
          }}
        >
          {navRoute.icon}
        </ListItemIcon>
        <ListItemText
          primary={navRoute.name}
          sx={{
            lineHeight: '1.8',
            padding: '5px 5px 5px 0',
          }}
          disableTypography
        />
        {level === 1
          ? (Boolean(anchor) && <FontAwesomeIcon icon={faChevronUp} />) || (
              <FontAwesomeIcon icon={faChevronDown} />
            )
          : (Boolean(anchor) && <FontAwesomeIcon icon={faChevronRight} />) || (
              <FontAwesomeIcon icon={faChevronDown} />
            )}
      </ListItem>

      <Popover
        sx={{
          top: level === 1 ? 10 : -5,
          left: level === 1 ? 0 : 5,
          inset: '-4px 0px 0px 0px !important',
        }}
        id={navRoute.name}
        anchorEl={anchor}
        keepMounted
        open={Boolean(anchor)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: (level === 1 && 'bottom') || 'bottom',
          horizontal: (level === 1 && 'left') || 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <List
          sx={{
            padding: '0',
            minWidth: '175px',
          }}
        >
          {(navRoute?.children || []).map((c, key) => (
            <Navigation
              key={`${c.name}-${key}`}
              navRoute={c}
              level={level + 1}
              path={`${path}${c.path}`}
              closeParentMenu={closeMenu}
            />
          ))}
        </List>
      </Popover>
    </div>
  );
};

export default Navigation;
